/**
 * 批量上报持久化存储模块
 * @description 使用IndexedDB存储失败的打点数据，确保数据不丢失
 */

class BatchStorage {
  constructor(config) {
    this.config = config;
    this.dbName = 'biPointBatchDB';
    this.storeName = 'failedPoints';
    this.db = null;
    this.initPromise = this.initDB();
  }

  /**
   * 统一就绪检查：等待初始化完成并校验 db 可用
   * @returns {Promise<boolean>} true 表示可安全进行 db 操作，false 表示不可用（不支持或初始化失败）
   */
  async _ensureReady() {
    await this.initPromise;
    return this.db != null;
  }

  /**
   * 初始化IndexedDB
   * @returns {Promise<IDBDatabase|null>} 不支持或打开失败时返回 null，调用方需检查 this.db
   */
  async initDB() {
    // 检查是否支持IndexedDB，不支持时显式保持 this.db 为 null
    if (!window.indexedDB) {
      console.warn('IndexedDB not supported, persistence disabled');
      this.db = null;
      return null;
    }

    return new Promise((resolve, reject) => {
      const request = indexedDB.open(this.dbName, 1);

      request.onerror = () => {
        console.error('IndexedDB open error:', request.error);
        this.db = null;
        resolve(null);
      };

      request.onsuccess = () => {
        this.db = request.result;
        resolve(this.db);
      };

      request.onupgradeneeded = event => {
        const db = event.target.result;
        if (!db.objectStoreNames.contains(this.storeName)) {
          const objectStore = db.createObjectStore(this.storeName, {
            keyPath: 'id',
            autoIncrement: true
          });
          objectStore.createIndex('timestamp', 'timestamp', { unique: false });
          objectStore.createIndex('retryCount', 'retryCount', {
            unique: false
          });
        }
      };
    });
  }

  /**
   * 保存失败的打点数据
   * @param {Array} points - 打点数据数组
   */
  async saveFailedPoints(points) {
    if (!this.config.storage.enabled || !points || points.length === 0) {
      return;
    }
    if (!(await this._ensureReady())) return;

    try {
      const transaction = this.db.transaction([this.storeName], 'readwrite');
      const objectStore = transaction.objectStore(this.storeName);

      for (const point of points) {
        const record = {
          data: point,
          timestamp: Date.now(),
          retryCount: point._retryCount || 0
        };
        objectStore.add(record);
      }

      // 控制存储数量
      await this.cleanupOldRecords();
    } catch (error) {
      console.error('Save failed points error:', error);
    }
  }

  /**
   * 获取失败的打点数据
   * @param {Number} limit - 获取数量限制
   * @returns {Array} 失败的打点数据
   */
  async getFailedPoints(limit = 50) {
    if (!this.config.storage.enabled) return [];
    if (!(await this._ensureReady())) return [];

    return new Promise((resolve, reject) => {
      try {
        const transaction = this.db.transaction([this.storeName], 'readonly');
        const objectStore = transaction.objectStore(this.storeName);

        const onRecords = records => {
          resolve(records.map(record => ({
            ...record.data,
            _storageId: record.id
          })));
        };

        // IE11 等旧浏览器不支持 getAll，使用 openCursor 兼容
        if (typeof objectStore.getAll === 'function') {
          const request = objectStore.getAll();
          request.onsuccess = () => {
            const records = (request.result || []).slice(0, limit);
            onRecords(records);
          };
          request.onerror = () => {
            console.error('Get failed points error:', request.error);
            resolve([]);
          };
        } else {
          const records = [];
          const request = objectStore.openCursor();

          request.onsuccess = event => {
            const cursor = event.target.result;
            if (cursor && records.length < limit) {
              records.push(cursor.value);
              if (records.length < limit) {
                cursor.continue();
              } else {
                onRecords(records);
              }
            } else {
              onRecords(records);
            }
          };

          request.onerror = () => {
            console.error('Get failed points error:', request.error);
            resolve([]);
          };
        }
      } catch (error) {
        console.error('Get failed points error:', error);
        resolve([]);
      }
    });
  }

  /**
   * 删除指定的打点记录
   * @param {Array} points - 要删除的打点数据（含 _storageId 的记录可批量按主键删除，性能更优）
   */
  async deletePoints(points) {
    if (!this.config.storage.enabled || !points || points.length === 0) {
      return;
    }
    if (!(await this._ensureReady())) return;

    const idsToDelete = points
      .map(p => p._storageId)
      .filter(id => id != null);

    if (idsToDelete.length === 0) return;

    return new Promise((resolve, reject) => {
      try {
        const transaction = this.db.transaction([this.storeName], 'readwrite');
        const objectStore = transaction.objectStore(this.storeName);

        idsToDelete.forEach(id => objectStore.delete(id));

        transaction.oncomplete = () => resolve();
        transaction.onerror = () => {
          console.error('Delete points error:', transaction.error);
          reject(transaction.error);
        };
      } catch (error) {
        console.error('Delete points error:', error);
        reject(error);
      }
    });
  }

  /**
   * 关闭 IndexedDB 连接，释放资源
   */
  close() {
    if (this.db) {
      this.db.close();
      this.db = null;
    }
  }

  /**
   * 清理过期和超量记录
   */
  async cleanupOldRecords() {
    if (!(await this._ensureReady())) return;

    try {
      const transaction = this.db.transaction([this.storeName], 'readwrite');
      const objectStore = transaction.objectStore(this.storeName);
      const countRequest = objectStore.count();

      countRequest.onsuccess = () => {
        const count = countRequest.result;
        const maxSize = this.config.storage.maxStorageSize;

        if (count > maxSize) {
          // 删除最旧的记录
          const index = objectStore.index('timestamp');
          const cursorRequest = index.openCursor();
          let deleteCount = count - maxSize;

          cursorRequest.onsuccess = event => {
            const cursor = event.target.result;
            if (cursor && deleteCount > 0) {
              cursor.delete();
              deleteCount--;
              cursor.continue();
            }
          };
        }
      };

      // 清理过期记录
      const now = Date.now();
      const expireTime = this.config.storage.expireTime;
      const index = objectStore.index('timestamp');
      const range = IDBKeyRange.upperBound(now - expireTime);
      const cursorRequest = index.openCursor(range);

      cursorRequest.onsuccess = event => {
        const cursor = event.target.result;
        if (cursor) {
          cursor.delete();
          cursor.continue();
        }
      };
    } catch (error) {
      console.error('Cleanup old records error:', error);
    }
  }

  /**
   * 清空所有记录
   */
  async clearAll() {
    if (!(await this._ensureReady())) return;

    try {
      const transaction = this.db.transaction([this.storeName], 'readwrite');
      const objectStore = transaction.objectStore(this.storeName);
      objectStore.clear();
    } catch (error) {
      console.error('Clear all records error:', error);
    }
  }
}

export default BatchStorage;
